import React, { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Editor from '@monaco-editor/react';
import deepEqual from 'fast-deep-equal';
import CodeStep from '../../../models/Steps/CodeStep.ts';
import { 
  TextField, 
  Button, 
  Box, 
  Chip, 
  Tooltip, 
  CircularProgress, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Typography,
  Paper,
  IconButton,
  Divider
} from '@mui/material';
import { Plus, Minus, Wand2, Maximize2, Minimize2 } from 'lucide-react';
import { useEnvironment } from '../../../context/EnvironmentContext';
import WorkflowService from '../../../services/WorkflowService';
import Autocomplete from '@mui/material/Autocomplete';

interface CodeStepBoxProps {
  stepData: any;
  onUpdate?: (step: any) => void;
  onUpdateImmediate?: (step: any) => void;
  workflowInputs?: {};
}

const CodeStepBox: React.FC<CodeStepBoxProps> = ({ 
  stepData, 
  onUpdate,
  onUpdateImmediate,
  workflowInputs = {}
}) => {
  // UI-only state
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCodeMaximized, setIsCodeMaximized] = useState(false);
  const [isPromptMaximized, setIsPromptMaximized] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [variableModalOpen, setVariableModalOpen] = useState(false);
  const [selectedVariables, setSelectedVariables] = useState<string[]>([]);
  const monacoRef = useRef<any>(null);

  // Handlers
  const handleChange = (field: string, value: any) => {
    if (!onUpdate) return;
    const definition = {
      ...stepData.definition,
      [field]: value
    };
    const updatedStep = new CodeStep(
      field === 'description' ? value : stepData.description,
      definition
    );
    updatedStep.id = stepData.id;
    onUpdate(updatedStep);
  };

  const handleCodeChange = (newCode: string | undefined) => {
    handleChange('code', newCode || '');
  };

  const handleGenerateCode = async () => {
    if (!prompt.trim()) return;
    setIsGenerating(true);
    try {
      // You may want to use your actual code generation logic here
      const generatedCode = `# AI Generated Code for: ${prompt}\nprint('Hello from AI!')`;
      let newCode = (stepData.definition.code || '').trim();
      newCode += (newCode ? '\n\n' : '') + generatedCode;
      handleChange('code', newCode);
      setPrompt('');
    } finally {
      setIsGenerating(false);
    }
  };

  // UI rendering
  return (
    <Paper sx={{ p: 2, bgcolor: 'grey.100', border: 1, borderColor: 'grey.300', fontSize: '0.875rem' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <IconButton
            onClick={() => setIsCollapsed(v => !v)}
            size="small"
            sx={{ mr: 1 }}
            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? '▶' : '▼'}
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Code Step: Python Code</Typography>
        </Box>
      </Box>
      {!isCollapsed && (
        <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'grey.700' }}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>ID:</Typography>
            <TextField
              value={stepData.id}
              InputProps={{ readOnly: true }}
              sx={{ bgcolor: 'grey.200', '& .MuiInputBase-input': { cursor: 'not-allowed' } }}
              size="small"
              fullWidth
            />
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Description:</Typography>
            <TextField
              value={stepData.description}
              onChange={e => handleChange('description', e.target.value)}
              size="small"
              fullWidth
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>AI Code Generation:</Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="body2">Prompt Editor</Typography>
              <IconButton
                size="small"
                onClick={() => setIsPromptMaximized(true)}
              >
                {isPromptMaximized ? <Minimize2 width={16} height={16} /> : <Maximize2 width={16} height={16} />}
              </IconButton>
            </Box>
            <Box display="flex" gap={1}>
              <TextField
                multiline
                placeholder="Describe the code you want to generate..."
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                minRows={2}
                maxRows={4}
                sx={{ flexGrow: 1 }}
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleGenerateCode}
                disabled={isGenerating}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                {isGenerating ? (
                  <CircularProgress size={16} />
                ) : (
                  <Wand2 width={16} height={16} />
                )}
                <Typography sx={{ ml: 0.5 }}>Generate</Typography>
              </Button>
            </Box>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Python Code:</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Button size="small" variant="outlined" onClick={() => setVariableModalOpen(true)}>
                  Insert Variable
                </Button>
                {Object.entries(workflowInputs).map(([key]) => (
                  <Tooltip title="Referenced Variables" key={key}>
                    <Box display="flex" gap={0.5}>
                      <Chip label={key} color="error" size="small" />
                    </Box>
                  </Tooltip>
                ))}
                <IconButton size="small" onClick={() => setIsCodeMaximized(true)}>
                  {isCodeMaximized ? <Minimize2 width={16} height={16} /> : <Maximize2 width={16} height={16} />}
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
              <Editor
                height="200px"
                defaultLanguage="python"
                value={stepData.definition.code || ''}
                onChange={handleCodeChange}
                theme="vs-light"
                options={{
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  lineNumbers: 'on',
                  renderLineHighlight: 'all',
                  automaticLayout: true
                }}
                onMount={(editor) => { monacoRef.current = editor; }}
              />
            </Box>
          </Box>
          <Box sx={{ mb: 1 }}>
            {!(stepData.definition.condition) ? (
              <Button 
                variant="outlined" 
                startIcon={<Plus width={16} height={16} />}
                onClick={() => handleChange('condition', '')}
                fullWidth
              >
                Add Condition
              </Button>
            ) : (
              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Step Condition (Python Code):</Typography>
                  <Button 
                    variant="text" 
                    color="error"
                    startIcon={<Minus width={16} height={16} />}
                    onClick={() => handleChange('condition', undefined)}
                  >
                    Remove
                  </Button>
                </Box>
                <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                  <Editor
                    height="60px"
                    defaultLanguage="python"
                    value={stepData.definition.condition || ''}
                    onChange={val => handleChange('condition', val)}
                    theme="vs-light"
                    options={{
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      fontSize: 14,
                      lineNumbers: 'off',
                      glyphMargin: false,
                      folding: false,
                      lineDecorationsWidth: 0,
                      lineNumbersMinChars: 0,
                      automaticLayout: true
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
      <Dialog
        open={isCodeMaximized}
        onClose={() => setIsCodeMaximized(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: '90vh', mt: 2.5 } }}
      >
        <DialogTitle>Code Editor</DialogTitle>
        <DialogContent>
          <Box sx={{ height: 'calc(100% - 60px)' }}>
            <Editor
              height="calc(100% - 60px)"
              defaultLanguage="python"
              value={stepData.definition.code || ''}
              onChange={handleCodeChange}
              theme="vs-light"
              options={{
                minimap: { enabled: true },
                scrollBeyondLastLine: true,
                fontSize: 14,
                lineNumbers: 'on',
                renderLineHighlight: 'all',
                automaticLayout: true
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isPromptMaximized}
        onClose={() => setIsPromptMaximized(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: '90vh', mt: 2.5 } }}
      >
        <DialogTitle>Prompt Editor</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="Describe the code you want to generate..."
            minRows={10}
            maxRows={20}
            fullWidth
            sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPromptMaximized(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateCode}
            disabled={isGenerating}
            startIcon={isGenerating ? <CircularProgress size={16} /> : <Wand2 width={16} height={16} />}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={variableModalOpen} onClose={() => setVariableModalOpen(false)}>
        <DialogTitle sx={{ fontWeight: 'bold', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          Insert Workflow Variables
        </DialogTitle>
        <DialogContent sx={{ minWidth: 350, p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select one or more workflow input variables to insert at the cursor position in your code.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Autocomplete
            multiple
            options={Object.keys(workflowInputs)}
            value={selectedVariables}
            onChange={(_, newValue) => setSelectedVariables(newValue)}
              renderTags={(value: string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip label={option} {...getTagProps({ index })} color="primary" />
                ))
              }
            renderInput={(params) => (
              <TextField {...params} label="Workflow Inputs" placeholder="Select variables" />
            )}
            disableCloseOnSelect
            noOptionsText="No workflow inputs available"
          />
          </Paper>
          <Divider sx={{ my: 2 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setVariableModalOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              let newCode = stepData.definition.code || '';
              const insertText = selectedVariables.map(v => `{${v}}`).join(' ');
              if (monacoRef.current) {
                const editor = monacoRef.current;
                const selection = editor.getSelection();
                const pos = selection ? editor.getModel().getOffsetAt(selection.getStartPosition()) : newCode.length;
                newCode = newCode.slice(0, pos) + insertText + newCode.slice(pos);
              } else {
                newCode += (newCode && !newCode.endsWith(' ')? ' ' : '') + insertText;
              }
              handleCodeChange(newCode);
              setVariableModalOpen(false);
              setSelectedVariables([]);
            }}
            disabled={selectedVariables.length === 0}
            variant="contained"
            color="primary"
            sx={{ borderRadius: 2, fontWeight: 600, px: 3 }}
          >
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default React.memo(CodeStepBox);